import * as React from "react";
import { put, call, takeLatest, select, take } from "redux-saga/effects";
import * as Api from "../api";
import * as ActionConstants from "../actions/actionConstants";
import { SHOW_TOAST_WITH_PROPS } from '../../../common/actionConstants';
import { HIDE_DIALOGUE_BOX } from "../../../components/DialogueBox/actionConstants";
import { StringConstants, CharConstants, NOTES_ERROR_CODES, RightPanelConstant,MiddlePanelConstant,ErrorCode } from '../constants';
import { logout } from './common';
import { updatePanelDataPermissionSet } from "./middlePanelContainer";
import { BUTTON_LOADER } from "../actions/actionConstants";
import  * as PUBLIC_API_NOTE_ACTIONS  from "../../../publicApiActions/noteActions";
import { getGuidWdoutExt, getAxiosError } from '../../../common/util';

// Helpers
const enrichedNoteRespones = (res, payload) => ({
    noteTemplate: {
        ...res.template,
        customFields: res.fields
    },
    moduleData: {
        moduleType: payload.moduleType,
        ...res.moduleContext,
        ...res.privacy
    }
});

const extractReferrer = (payload) => {
    const { referrer, ...rest } = payload;
    return { referrer: referrer === "views" ? referrer : "", payload: rest };
};

export function* getAllNoteTemplates({ payload: rawPayload }) {
    const { referrer, payload } = extractReferrer(rawPayload);

    try {
        yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: { isLoading: true } });

        const response = yield call(Api.getAllNoteTemplates, payload);        
        if (response.status === 200) {
          yield* handleNoteTemplatesSuccess(response, payload);
        } else if (response.status === 203 && referrer === "views") {
            yield put({ type: ActionConstants.SAVE_NOTE_ERROR, payload: { SaveNoteErrorTextByViews: response.error } });
        } else if (response.status === ErrorCode.RedirectToLogout) {
            yield* logout();
        } else {
            yield put({
                type: ActionConstants.LOG_APPLICATON_ERROR,
                payload: { status: response.status, isErrorScreen: true }
            });
        }
    } catch (error) {
        console.error('getAllNoteTemplates failed:', error);
        yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: { isLoading: false } });
    }
}

function* handleNoteTemplatesSuccess(response, payload) {
    const { Data, moduleId, moduleTemplateId, noteTemplates } = response;
    yield put({
        type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS,
        payload: {
            noteTemplates: Data.NoteTemplates,
            templateType: payload.nttype
        }
    });
    
    const isWorkkard = payload.nttype === CharConstants.Workkard;
    const isCreateNoteActivity = payload?.requestFrom === "create_note" && payload.nttype === CharConstants.Activity;

    if (isWorkkard || isCreateNoteActivity) {
        const selectedNotePayload = {
            moduleId,
            moduleType: isWorkkard ? CharConstants.Workkard : CharConstants.Activity,
            notetemplateId: isWorkkard
                ? moduleTemplateId
                : noteTemplates?.find(item => item.name === "File Note")?.id || noteTemplates?.[0]?.id
        };
        yield* getSelectedNoteTemplateData({ payload: selectedNotePayload });
    }
    yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: { isLoading: false } });
}

export function* updateCreateNoteRedux(data) {
  try {
      yield put({
          type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS,
          payload: {
              templateType: data.moduleData.moduleType,
              noteData: {...data.noteTemplate, ...data.moduleData},
          }
      });
      yield put({
          type: ActionConstants.PANEL_VISIBILITY_DATA_SUCCESS,
          payload: {
              isLeftVisible: false,
              isRightVisible: false,
              isNoteVisible: true,
              isNoteCreationVisible: true,
              showWkInMobileView: true
          }
      });
  } catch (error) {
      console.error('updateCreateNoteRedux failed:', getAxiosError ? getAxiosError(error).error : error);
  }
}

export function* getSelectedNoteTemplateData({ payload }) {
    try {
        yield put({ type: ActionConstants.TOGGLE_CREATE_NOTE_LOADING, payload: { isLoading: true } });
        const res = yield call(Api.getSelectedNoteTemplateData, payload);
        const updatedRes = enrichedNoteRespones(res, payload);
        if (updatedRes) {
            yield* updateCreateNoteRedux(updatedRes);
        }
    } catch (error) {
        console.error('getSelectedNoteTemplateData failed:', error);
    } finally {
        yield put({ type: ActionConstants.TOGGLE_CREATE_NOTE_LOADING, payload: { isLoading: false } });
    }
}

function* showNoteCreationErrorToast() {
  yield put({
    type: SHOW_TOAST_WITH_PROPS,
    payload: {
      message: "Error while note creation",
      iconType: "error",
      delayTime: 5000,
      autoClose: true
    }
  });
}

export function* publishNote({ payload }) {
  try {
    yield put({ type: BUTTON_LOADER, payload: { publishNote: true } });
    const { callback, ...noteFields } = payload;

    const fileAttachments = (noteFields.NoteFileJson ?? [])
      .filter((item) => item.guid)
      .map((item) => ({ caption: item.caption, guid: item.guid }));

    const notePayload = {
      data: {
        privacy: noteFields.hdnNotePrivacy === "_private" ? "community" : "public",
        description: noteFields.txtareaAddNote,
        attachments: fileAttachments,
        template_type: noteFields.templateType,
        template_fields: noteFields.templateFields
      },
      moduleId: noteFields.itemId,
      module: noteFields.type,
      headers: { XTemplateSharingEnabled: noteFields.templateSharingEnabled }
    };
    const response = yield call(PUBLIC_API_NOTE_ACTIONS.addNote, notePayload);
    if (!response?.note_id) {
      yield* showNoteCreationErrorToast();
      return;
    }

    yield* setPanelVisibilityAndNoteState();
    callback?.();
    
    const toastPayload = {
      message: "Note created successfully.",
      iconType: "success",
      delayTime: 5000,
      autoClose: true,
      ...((!noteFields.showToastAction)
        ? { actId: noteFields.currentActivity, entityType: noteFields.type, showActions: true } : {})
    };

    yield put({ type: SHOW_TOAST_WITH_PROPS, payload: toastPayload });
    yield take(ActionConstants.FETCH_MIDDLE_PANEL_NOTES_SUCCESS);
  }
  catch (error) {
    console.error('publishNote failed:', error);
    yield* showNoteCreationErrorToast();
  }
  finally {
    yield put({ type: BUTTON_LOADER, payload: { publishNote: false }});
  }
}

export function* fetchWorkkardActNoteFiles({ payload }) {
  try {
    yield put({
      type: ActionConstants.GET_WORKKARD_ACTIVITY_NOTE_FILES_SUCCESS,
      payload: {
        isLoading: true,
        filesData: []
      }
    })
    let response = yield call(PUBLIC_API_NOTE_ACTIONS.fetchWorkkardActNoteFiles, payload);
    if(response.length) {
      yield put({
        type: ActionConstants.GET_WORKKARD_ACTIVITY_NOTE_FILES_SUCCESS,
        payload: {
          isLoading: false,
          filesData: response
        }
      })
    }
  }catch(error) {
    console.error('getSelectedNoteTemplateData failed:', error);
  }finally {
    yield put({ type: ActionConstants.GET_WORKKARD_ACTIVITY_NOTE_FILES_SUCCESS, payload: { isLoading: false } });
  }
}

// -----------------------------------------------



export function* discardSelectedNoteTemplate({payload} ) {
  try {
    const param = { draftNoteId: payload };
    yield call(Api.discardSelectedNoteTemplate, param);
    const getItems = (state) => state.workkardDetail;
    const items = yield select(getItems);
    const panelVisibility = {
      isLeftVisible: (items.panelVisibility.isFullViewActive || items.panelVisibility.isVisibleExternalApp) ? false : true,
      isRightVisible: true,
      isNoteVisible: false,
      isNoteCreationVisible: false,
      showWkInMobileView: items.panelVisibility.templateType != 'W'? false: true
    }
    yield put({ type: ActionConstants.PANEL_VISIBILITY_DATA_SUCCESS, payload: panelVisibility });
    yield put({ type: ActionConstants.RESET_NOTE_CREATION_STATE });
  }
  catch (error) {
    yield put({ type: ActionConstants.PANEL_VISIBILITY_DATA_ERROR, payload: payload });
  }
}


// Changed to Public Api
export function* publishSavedNote({payload}) {
  try {
    yield put({ type: ActionConstants.SHOW_MIDDLE_PANEL_LOADER });

    yield put ({
      type: ActionConstants.UPDATE_REFRESH_NOTE_ID,
      payload: {id:payload.nId}
    })

    let response,param = null;
    const getItems = (state) => state.workkardDetail;
    const items = yield select(getItems);
    let visibility = payload.publishType == StringConstants.Public ? "public" : "community";
    let notePayload  = {
      module : payload.templateType === StringConstants.activity ? "activity" : "workkard",
      note_id : payload.nId,
      data : {
                "visibility": visibility
             }
    }

    response = yield call(PUBLIC_API_NOTE_ACTIONS.publishNote, notePayload);

    if (response.status == 200){
      
      const param = {
        id: payload.nId,
        Type: payload.publishType.replace("_", "") === StringConstants.PublicText.toLowerCase() ? StringConstants.PublicText : StringConstants.InternalText,
        CanEditNote: false,
        CanCloneNote: response.canCloneNotes,
        CanDeleteNote: response.canDeleteNotes
      }
      yield put({ type: ActionConstants.NOTES_ACTION_PUBLISH_SUCCESS, payload: param })

      const tempParam = {}
      tempParam.request={
        type: payload.templateType,
        skip: 0,
        take: items.middlePanel.notes.skip + items.middlePanel.notes.take||10,
        sortBy: payload.templateType == StringConstants.activity ? "0" : "1",
        reviewFilter:"0",
        InternalToPublic:true,
        
      }
      if(payload.templateType == StringConstants.activity)
      {
        tempParam.request.actId = payload.itemId;
      }
      else {
        tempParam.request.cId = payload.wkId
      }

        yield* updatePanelDataPermissionSet({ payload: tempParam , permission:{isFetchNotes:true}});
        
    } else if(status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    }
    else if(response.status==ErrorCode.Fail && response.toastPayload)
     {
        yield put({type:SHOW_TOAST_WITH_PROPS,payload:response.toastPayload});
     } else {
      if(response.error){
        yield put({ type: ActionConstants.SAVE_NOTE_ERROR, payload: { saveNoteErrorText: response.error } })
      }
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:status, isErrorScreen:false  }
        });
    }    
  } catch (error) {
    console.log(getAxiosError(error).error);
  }
  finally {
    yield put({ type: ActionConstants.HIDE_MIDDLE_PANEL_LOADER });
  }
}

export function* getAllDraftNotes(noteTemplate, type, draftNote = null,tzdata=[], actData = null, wkData = null, noteTemplates = null, activityData = null){
  try{
    //const draftData = yield call(Api.getAllDraftNotes, ActionConstants.GET_ALL_DRAFT_NOTES)
    //if (true) {
      if (type == CharConstants.Activity && noteTemplates){
        const selectedNoteTemplateData = {
          templateType: type,
          selectedNoteDraftandTemplateData: { DraftNote: draftNote, NoteTemplate: noteTemplate, ActivityData: activityData },
          activitiesInfo: actData,
          noteTemplates: noteTemplates,
          tzData: tzdata
        }
        yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: selectedNoteTemplateData });
      }
      else if (type == CharConstants.Activity ){
        const selectedNoteTemplateData = {
          templateType: type,
          selectedNoteDraftandTemplateData: { DraftNote: draftNote, NoteTemplate: noteTemplate , ActivityData: activityData},
          activitiesInfo: actData,
          tzData: tzdata
        }
        yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: selectedNoteTemplateData });
      }
      else {
        const noteTemplateListingData = {
          templateType: type,
          noteTemplates: noteTemplates,
          createdDraftNote: {
            DraftNote: draftNote,
            NoteTemplate: noteTemplates,
            WorkkardData: wkData,
          },
          tzData: tzdata
        }
        yield put({ type: ActionConstants.NOTE_TEMPLATES_DATA_SUCCESS, payload: noteTemplateListingData });
      }
      const panelVisibility = {
        isLeftVisible: false,
        isRightVisible: false,
        isNoteVisible: true,
        isNoteCreationVisible: true,
        showWkInMobileView: true
      }
      yield put({ type: ActionConstants.PANEL_VISIBILITY_DATA_SUCCESS, payload: panelVisibility });   
  }
  catch(error){
    console.log(error);
  }
}

export function* getSavedNoteClonedToDraftNote({ payload }) {
  try {
    // yield* discardDraftNotes()
    const { Data, NoteTemplates, actData, WorkkardData, ActivityData, tzData ,status } = yield call(Api.cloneToDraftNote, payload);
    let noteTemplate = Data.NoteTemplate
    if (status === 200) {
      yield put({ type: ActionConstants.CHANGE_TEMPLATE_TYPE, payload: { templateType: payload.type } })
      yield* getAllDraftNotes(noteTemplate, payload.type, Data.DraftNote, tzData, actData, WorkkardData, NoteTemplates, ActivityData)
    } else if(status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    } else {
      if (response.error) {
        yield put({ type: ActionConstants.SAVE_NOTE_ERROR, payload: { saveNoteErrorText: response.error } })
      }
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:status, isErrorScreen:false  }
        });
    }    
  }
  catch (error) {
  }
}

export function* getSavedDraftNoteForEdit({ payload }) {
  try {
    //yield* discardDraftNotes()
    const { Data, status } = yield call(Api.editDraftNote, payload);
    let noteTemplate = Data.NoteTemplate
    if (status == 200) {
      yield put({ type: ActionConstants.CHANGE_TEMPLATE_TYPE, payload: { templateType: payload.type}})
      yield* getAllDraftNotes(noteTemplate, payload.type, Data.DraftNote)
    }else if(status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    } else {
      if (response.error) {
        yield put({ type: ActionConstants.SAVE_NOTE_ERROR, payload: { saveNoteErrorText: response.error } })
      }
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:status, isErrorScreen:false  }
        });
    }    
  }
  catch (error) {

  }
}

export function* deleteSavedNote({ payload }) {
  try {
    let actionConstant = ActionConstants.DELETE_SAVED_NOTE_REQUEST;
    if (payload.type == CharConstants.Workkard)
      actionConstant = ActionConstants.DELETE_SAVED_WORKKARD_NOTE_REQUEST;
    const { status } = yield call(Api.deleteSavedNote, actionConstant, payload);
    if (status === 200) {
      const param = {
        nId: payload.nId,
        actId: payload.itemId
      }
      if(payload.type == CharConstants.Workkard){
        const getItems = (state) => state.workkardDetail;
        const items = yield select(getItems);
        const notesTabData=items.middlePanel.notes;
       if(notesTabData.data.filter(item=>item.Id==payload.nId)[0].WorkkardFiles.length >0){
        const paramFiles={
          type: 'workkard',
          cId: items.workkardDetail.Id,
          actId:0,
          skip: 0,
          take: 100,
          sortBy: "1",
          largeView: false,
          isSelectAll: false,
          filterOption: JSON.stringify(["Document", "Photo", "Audio", "Video", "Email", "Other", 'workkard'])
        }
        let Data = yield call(Api.getWorkkardFilesData, ActionConstants.FETCH_MIDDLE_PANEL_WORKKARD_FILE_SUCCESS, paramFiles);
        if(Data.status == 200){
          yield put({ type: ActionConstants.WORKKARD_DETAIL_MASTER_DATA_SUCCESS, payload:{workkardFiles:Data }});
        }
      }
    }

      yield put({ type: ActionConstants.NOTES_ACTION_DELETE_SUCCESS, payload: param })
      
      const getNotesData = (state)=>state.workkardDetail.middlePanel.notes;
      const getActivityDetailData=(state)=>state.workkardDetail.activityDetail;

      const notesData=yield select(getNotesData);

      const activityDetailData=yield select(getActivityDetailData);

      if (notesData.data.length < notesData.total) {
        const payload = {
          total: notesData.total,
          skip: notesData.skip || 0,
          take: notesData.take || 10,
          reviewFilter: notesData.reviewFilter,
          type: notesData.type,
          actId: activityDetailData.activityId,
          cId: activityDetailData.workkardId,
          sortBy: "0"
        }

        yield put({ type: ActionConstants.FETCH_MIDDLE_PANEL_NOTES_REQUEST, payload })
      }
    }else if(status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    } else {
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:status, isErrorScreen:false  }
        });
    }    
  } catch (error) {

  }
}

// Changed to Public Api
export function* saveDraftNote({ payload }) {
  try {
    const {callback} = payload;
    delete payload.callback;

    const fileAttachments = getGuidWdoutExt(payload.NoteFileJson);
    

    let notePayload = {
      data : {
        privacy :  "draft",
        description: payload.txtareaAddNote,
        attachments : fileAttachments.length > 0 ? fileAttachments : [],
        template_type: payload.templateType,
        template_fields: payload.templateFields
      },
      moduleId: payload.itemId, 
      module: payload.type,
      headers: {XTemplateSharingEnabled:payload.templateSharingEnabled}  
    }

    
   let response = yield call(PUBLIC_API_NOTE_ACTIONS.addNote, notePayload);


    if (response.status == 200) {
      yield put({ type: HIDE_DIALOGUE_BOX});
      yield* setPanelVisibilityAndNoteState();
      callback();

      const getItems = (state) => state.workkardDetail;
      const items = yield select(getItems);
      const param = {
        type: items.middlePanel.notes.type,
        skip: 0,
        take: items.middlePanel.notes.take,
        sortBy: payload.type == StringConstants.activity ? "0" : "1",
        reviewFilter: items.middlePanel.notes.reviewFilter
      }
      if(payload.type == StringConstants.activity)
        param.actId = payload.itemId
      else
        param.cId = payload.itemId

      const toastPayload={
        message:"Draft Note Created successfully.",
        type:"success",
        delayTime:5000,
        entityType: payload.type,
        cId: payload.type == StringConstants.activity ? items.workkardDetail.Id : parseInt(payload.itemId),
        actId: payload.type == StringConstants.activity ? parseInt(payload.itemId) : items.activityDetail.activityId ? items.activityDetail.activityId : 0,
        showActions: true,
        autoClose: true
      }
      if ((payload.type == StringConstants.activity && items.activityDetail.activityId != payload.itemId) ||
        (payload.type == StringConstants.activity && items.middlePanel.notes.type != payload.type) ||
        (payload.type == StringConstants.workkard && items.middlePanel.notes.type != payload.type) ||
        items.middlePanel.activeTab != 0 || items.panelVisibility.toggleView == RightPanelConstant.CombinedViewText){
        yield put({type:SHOW_TOAST_WITH_PROPS,payload:toastPayload})
      }

      if (payload.type == items.middlePanel.notes.type){
        yield* updatePanelDataPermissionSet({ payload: param , permission:{noteEdited:true}})
      }
      if (payload.NoteFileJson.length && (payload.type == items.middlePanel.files.type || items.middlePanel.files.type == MiddlePanelConstant.All)){
        const paramFiles={
          type: items.middlePanel.files.type,
          cId: payload.type == StringConstants.activity ? '' : payload.itemId,
          actId: payload.type == StringConstants.activity ? payload.itemId : '',
          skip: items.middlePanel.files.skip,
          take: items.middlePanel.files.take,
          sortBy: "0",
          largeView: false,
          isSelectAll: false,
          filterOption: JSON.stringify(["Document", "Photo", "Audio", "Video", "Email", "Other", payload.type])
        }
        yield* updatePanelDataPermissionSet({ payload: paramFiles , permission:{isUpdateFiles:true}})
      }      
    } else if (NOTES_ERROR_CODES.hasOwnProperty(status)){
      yield put({ type: ActionConstants.SAVE_NOTE_ERROR, payload: { saveNoteErrorText: NOTES_ERROR_CODES[status]}})
    } else if(status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    }
    else if(response.status==ErrorCode.Fail && response.toastPayload)
     {
        yield* setPanelVisibilityAndNoteState();
        yield put({type:SHOW_TOAST_WITH_PROPS,payload:response.toastPayload});
     }
     else {
        if (response.error) {
          yield put({ type: ActionConstants.SAVE_NOTE_ERROR, payload: { saveNoteErrorText: response.error } })
        }
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:status, isErrorScreen:false  }
        });
    }    
  }
  catch (error) {
    console.log(getAxiosError(error).error);
  }
  finally {

  }
}

export function* changeNoteModule({payload}) {
    try{
      const panelVisibility = {
        templateType: payload.module_type
      }
      yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: panelVisibility });
      yield* getAllNoteTemplates({payload: payload});
    }
    catch(error){

    }
}

export function* changeNoteCreationActivityTemplate({ payload }) {
    try {
      const { status, selectedActData } = yield call(Api.changeNoteCreationActivityTemplate, ActionConstants.CHANGE_NOTE_CREATION_ACTIVITY_TEMPLATE_REQUEST, payload)

      if (status === 200 || status === 204) {
        const getItems = (state) => state.workkardDetail;
        const items = yield select(getItems);
        selectedActData.newActNoteTemplate.DraftNote.Id = payload.draftNoteId;
        selectedActData.newActNoteTemplate.DraftNote.DraftType = "a";
        let activityData = Object.assign({}, items.createNote.selectedNoteDraftandTemplateData.ActivityData)
        activityData.psnl = selectedActData.psnl
        activityData.pub = selectedActData.pub
        activityData.pvt = selectedActData.pvt
        const selectedNoteTemplateData = {
          actTitle: selectedActData.actTitle,
          noteTemplates: selectedActData.newActNoteTemplate.DraftNote.AllNoteTemplates,
          selectedNoteDraftandTemplateData: { ...selectedActData.newActNoteTemplate, ActivityData: activityData }
        }
        yield put({ type: ActionConstants.SELECTED_NOTE_TEMPLATE_DATA_SUCCESS, payload: selectedNoteTemplateData });
      }
      else if(status ==  ErrorCode.RedirectToLogout){
        yield* logout();
      } else {
          yield put({
          type: ActionConstants.LOG_APPLICATON_ERROR,
          payload: { status:status, isErrorScreen:false  }
          });
      }    
    }
    catch {

    }
    finally {

    }
}

export function* discardDraftNotes() {
  const res = yield call(Api.getAllDraftsCount, ActionConstants.GET_ALL_DRAFTS_COUNT);
  if (res.status == 200 && res.Data > 0) {
    const param = { draftNoteId: res.existingDraftNotes[0].Id };
    const discardres = yield call(Api.discardSelectedNoteTemplate, param);
    if(discardres.status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    }
  }else if(res.status ==  ErrorCode.RedirectToLogout){
    yield* logout();
  } else {
      yield put({
      type: ActionConstants.LOG_APPLICATON_ERROR,
      payload: { status: res.status, isErrorScreen:false  }
      });
  }    
}

function * setPanelVisibilityAndNoteState() {
  const getItems = (state) => state.workkardDetail;
  const items = yield select(getItems);
  const panelVisibility = {
    isLeftVisible: (items.panelVisibility.isFullViewActive || items.panelVisibility.isVisibleExternalApp) ? false : true,
    isRightVisible: true,
    isNoteVisible: false,
    isNoteCreationVisible: false,
    showWkInMobileView: items.panelVisibility.templateType != 'W'? false: true
  }
  yield put({ type: ActionConstants.PANEL_VISIBILITY_DATA_SUCCESS, payload: panelVisibility });
  yield put({ type: ActionConstants.RESET_NOTE_CREATION_STATE });
}

export default [
  takeLatest(ActionConstants.GET_ALL_NOTE_TEMPLATES, getAllNoteTemplates),
  takeLatest(ActionConstants.GET_SELECTED_NOTE_TEMPLATE_DATA, getSelectedNoteTemplateData),
  takeLatest(ActionConstants.DISCARD_SELECTED_NOTE_TEMPLATE, discardSelectedNoteTemplate),
  takeLatest(ActionConstants.PUBLISH_NOTE, publishNote),
  takeLatest(ActionConstants.PUBLISH_SAVED_NOTE, publishSavedNote),
  takeLatest(ActionConstants.PUBLISH_SAVED_NOTE_INTERNAL, publishSavedNote),
  takeLatest(ActionConstants.CLONE_SAVED_NOTE_TO_DRAFT_REQUEST, getSavedNoteClonedToDraftNote),
  takeLatest(ActionConstants.EDIT_DRAFT_NOTE_REQUEST, getSavedDraftNoteForEdit),
  takeLatest(ActionConstants.DELETE_SAVED_NOTE_REQUEST, deleteSavedNote),
  takeLatest(ActionConstants.SAVE_DRAFT_NOTE, saveDraftNote),
  takeLatest(ActionConstants.CHANGE_NOTE_TEMPLATE_TYPE, changeNoteModule),
  takeLatest(ActionConstants.CHANGE_NOTE_CREATION_ACTIVITY_TEMPLATE_REQUEST, changeNoteCreationActivityTemplate),
  takeLatest(ActionConstants.GET_WORKKARD_ACTIVITY_NOTE_FILES, fetchWorkkardActNoteFiles)
];